import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../../apis/branch/BranchProvider";
import { useClient } from "../../../apis/client/ClientProvider";
import { getUiInterface, getPageName, getWordConnector, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import Branch from "../../../types/branch";
import { FaChevronDown } from "react-icons/fa";

const BranchCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postBranch } = useBranch();
    const { clients, fetchClient } = useClient();
    const navigate = useNavigate();
    const selectRef = useRef<(HTMLSelectElement | null)>(null);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const [branch, setBranch] = useState<Branch>({
        id: 0,
        name: "",
        cnpj: "",
        location: {
            id: 0,
            latitude: "",
            longitude: "",
            address: {
                id: 0,
                street: "",
                number: "",
                complement: "",
                city: "",
                state: "",
                country: "",
                zipCode: "",
            },
        },
        client: {
            id: 0,
            name: '',
            cnpj: '',
        }
    } as Branch);

    const navItems: NavBarItem[] = [
        { label: t('branch.labels.home'), route: "/", iconName: "house-door" },
        { label: t('branch.labels.branchList'), route: "/Branch", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setBranch((prevBranch) => {
            const keys = name.split(".");
            const updatedBranch = { ...prevBranch };
            let current: any = updatedBranch;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                current[key] = { ...current[key] };
                current = current[key];
            }

            current[keys[keys.length - 1]] = value;
            
            return updatedBranch;
        });
    };

    const handleClientChange = (e: React.ChangeEvent<any>) => {
        const { value } = e.target;

        setBranch((prevBranch) => {
            return {
                ...prevBranch,
                client: {
                    id: value
                }
            }
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!branch.name) nullRequiredFields.push("Name");
        if (!branch.client?.id) nullRequiredFields.push("Company");
        if (!branch.location?.latitude) nullRequiredFields.push("Lat");
        if (!branch.location?.longitude) nullRequiredFields.push("Lng");
        if (!branch.location?.address?.street) nullRequiredFields.push("Street");
        if (!branch.location?.address?.number) nullRequiredFields.push("Number");
        if (!branch.location?.address?.complement) nullRequiredFields.push("Complement");
        if (!branch.location?.address?.city) nullRequiredFields.push("City");
        if (!branch.location?.address?.state) nullRequiredFields.push("State");
        if (!branch.location?.address?.country) nullRequiredFields.push("Country");
        if (!branch.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

        if (nullRequiredFields.length > 0) {
            setInvalidFields(new Set(nullRequiredFields));
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        } else {
            setInvalidFields(new Set());
        }

        try {
            const response = await postBranch(branch);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createBranch", { id: response.data.id });
                navigate(`/Branch`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("createBranchError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    useEffect(() => {
        fetchClient(null);
    }, []);

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createBranch")}</h2>
            <Form 
                onSubmit={handleSubmit}
                style={{paddingBottom: "32px"}}
            >
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('branch.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={branch.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('branch.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={branch.cnpj}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('branch.fields.company')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    as="select"
                                    name="client"
                                    value={branch.client?.id}
                                    isInvalid={invalidFields.has("Company")}
                                    onChange={handleClientChange}
                                    ref={(el: HTMLSelectElement | null) => {
                                        if (el) selectRef.current = el;
                                    }}
                                    style={{ 
                                        backgroundImage: 'none', 
                                        appearance: "none", 
                                        paddingRight: "30px",
                                        cursor: "pointer" 
                                    }}
                                >
                                    <option value="">{t('branch.labels.selectCompany')}</option>
                                    {
                                        (clients || []).map((client) => (
                                            <option
                                                key={client.id}
                                                value={client.id}>
                                                {`Id: ${client.id} | Nome: ${client.name}`}
                                            </option>
                                        ))
                                    }
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "20px",
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => selectRef?.current?.focus()}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h5>{t('branch.fields.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('branch.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={branch.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('branch.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={branch.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('branch.fields.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('branch.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={branch.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('branch.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={branch.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('branch.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={branch.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('branch.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={branch.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('branch.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={branch.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('branch.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={branch.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>{t('branch.fields.zipCode')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={branch.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Button variant="primary" onClick={handleSubmit}>
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default BranchCreateView;